/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

/* eslint-disable object-curly-newline */

import React from 'react';
import {
    object, bool, number, func, array,
    shape, string, arrayOf, oneOfType,
} from 'prop-types';
import { compose } from 'recompose';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { connect } from 'react-redux';

import { withStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import ChevronRight from '@material-ui/icons/ChevronRight';

import { Tab, Tabs } from '@material-ui/core';
import Form from '../../Form';
import { form as FormStyle } from '../../../style/form';
import { isEmpty } from '../../../utils/object';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import { isArrayIdIncludes } from '../../../utils/array';
import InputsByLocation from '../AddressConfig/InputsByLocation';
import InputSchema from '../AddressConfig/InputSchema';

import Divider from '../../Divider';
import PhoneMask from '../InputMask/PhoneMask';
import CanadaZipCodeMask from '../InputMask/CanadaZipCodeMask';
import configENV from '../../../config';
import { countryISOAlfa3Codes } from '../../../state/constants/Countries';
import DesktopSelectAddressDropdown from '../../../components/AddressBook/DesktopAddressBook/DesktopSelectAddressDropdown';
import { getFeatureFlags } from '../../../utils/featureSelectors';

const { PERSIST_KEY } = configENV;

const styles = ({ typography, palette }) => FormStyle(typography, palette);
const INVALID_SECONDARY_UNIT_REASON = 'INVALID_SECONDARY_UNIT';
const loqateContainerCountRegex = /\s-\s(\d+\sAddresses?)$/i;
const normalizeCountryCode = (countryCode = '') => ({
    USA: 'US',
    CAN: 'CA',
}[countryCode] || countryCode || 'US');

const buildAddressDisplay = (address = {}) => {
    const primaryLine = address.addressLine1 || address.street || address.address1 || '';
    const tertiaryLine = address.addressLine2 || address.apt || address.address2 || '';
    const city = address.city || '';
    const state = address.state || '';
    const zipcode = address.zipcode || address.zipCode || '';
    const country = normalizeCountryCode(address.country || '');
    const secondaryLine = [city, state, zipcode].filter(Boolean).join(city && state ? ', ' : ' ').trim();

    return {
        primaryLine,
        tertiaryLine,
        secondaryLine: [secondaryLine, country].filter(Boolean).join(secondaryLine ? ', ' : ''),
    };
};

const getLoqateSuggestionDisplay = (address = {}) => {
    const baseText = address.text || '';
    const description = address.description || '';

    if (!address.isContainer) {
        return {
            label: description ? `${baseText}, ${description}` : baseText,
            meta: '',
        };
    }

    const countMatch = description.match(loqateContainerCountRegex);
    if (countMatch) {
        const locationText = description.replace(loqateContainerCountRegex, '').trim();
        return {
            label: locationText ? `${baseText}, ${locationText}` : baseText,
            meta: countMatch[1],
        };
    }

    return {
        label: description ? `${baseText}, ${description}` : baseText,
        meta: '',
    };
};

const getLoqateVerificationOptionLabel = (address = {}) => {
    if (address.isContainer) {
        const display = getLoqateSuggestionDisplay(address);
        const containerSummary = display.meta
            ? `${display.label} - ${display.meta}`
            : display.label;

        return `${containerSummary} >`;
    }

    if (address.normalizedAddress) {
        const formattedAddress = buildAddressDisplay(address.normalizedAddress);
        return [
            formattedAddress.primaryLine,
            formattedAddress.tertiaryLine,
            formattedAddress.secondaryLine,
        ].filter(Boolean).join(', ');
    }

    return getLoqateSuggestionDisplay(address).label;
};

class DesktopAddressForm extends Form {
    streetAddressColumnRef = React.createRef();

    state = {
        data: {
            firstname: '',
            lastname: '',
            relationship: '0',
            location: 'Residence',
            company: '',
            street: '',
            apt: '',
            apofpo: 'APO',
            city: '',
            state: '',
            zipcode: '',
            country: 'US',
            phone: '',
            isbilling: false,
            suggestedAddress: 0,
        },
        replaceMask: {
            phone: '[\\D]',
            zipcode: '*',
        },
        replaceMaskWith: {
            phone: '',
            zipcode: 'toUpperCase',
        },
        prevLocation: '',
        prevCity: '',
        prevCityAPO: '',
        prevState: '',
        prevStateAPO: '',
        inputConfig: {},
        errors: {},
        modalTitle: '',
        confirmAddress: false,
        selectedSearchAddress: false,
        showSuggestedDropdown: false,
        isVerificationNeeded: true,
        selectedRecipient: {},
        isRecipientSelected: false,
        activeTab: 'tab1',
        verificationModalResults: [],
        verificationModalHistory: [],
        verificationSelection: {
            type: 'entered',
            index: null,
        },
        activeVerificationContainerId: '',
        verificationDropdownOpen: false,
        isVerificationContainerLoading: false,
        suppressVerificationReopen: false,
        loqateTypeaheadResults: [],
        loqateTypeaheadHistory: [],
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const {
            modal: { title: modalTitle, config },
        } = this.props;
        this.schema = { ...InputSchema[config] };
        const confirmAddress = false;
        const selectedSearchAddress = false;
        const showSuggestedDropdown = false;
        const isVerificationNeeded = true;
        // Initialize state
        this.setState({ inputConfig: InputsByLocation[config], confirmAddress, selectedSearchAddress, showSuggestedDropdown, isVerificationNeeded });
        const {
            AddressId,
            recipient,
            recipient: { Addresses },
            locations,
            relationships,
        } = this.props;
        const { data } = this.state;

        if (config === 'BillingForm') {
            data.isbilling = true;
        }
        if (config === 'GiftListRecipientForm' && !isEmpty(recipient)) {
            const editData = {
                ...recipient.address,
                firstname: recipient.firstname || '',
                lastname: recipient.lastname || '',
                suggestedAddress: 0,
                relationship: 0,
                apofpo: 'APO',
                phone: recipient.phoneNumber || '',
            };
            editData.country = (editData.country) ? editData.country.id : 'US';
            editData.state = (editData.state) ? editData.state.id : '';
            editData.location = (editData.location) ? editData.location.name : 'Residence';
            delete editData.addressId;
            this.setState({ data: editData, modalTitle });
            return;
        }

        if (isEmpty(recipient) || (config !== 'GiftListRecipientForm' && !Addresses) || !this.state.inputConfig || !this.schema) {
            this.setState({ data, modalTitle });
            return;
        }
        const addressesById = Addresses.filter((a) => a.AddressId === AddressId);
        if (!addressesById.length) {
            this.setState({ data, modalTitle });
            return;
        }

        data.firstname = recipient.FirstName;
        data.lastname = recipient.LastName;
        data.relationship = this.getRelationship(
            relationships,
            recipient.RelationShip,
        );
        data.phone = recipient.PhoneNumber ? recipient.PhoneNumber.trim().replace(/[\D]/g, '') : '';
        data.location = isArrayIdIncludes(
            locations,
            addressesById[0].AddressType,
        )
            ? addressesById[0].AddressType
            : 'Residence';
        data.company = recipient.EmployerName ? recipient.EmployerName : '';
        data.street = addressesById[0].AddressLineOne;
        data.apt = addressesById[0].AddressLineTwo;
        data.city = addressesById[0].City;
        data.state = addressesById[0].StateProvince;
        data.zipcode = addressesById[0].PostalCode;
        if (addressesById[0].IsBillingAddress !== undefined) {
            data.isbilling = addressesById[0].IsBillingAddress;
        }
        // TODO: need to default country to US if it's a number value
        data.country = addressesById[0].Country;

        let { prevState, prevStateAPO, prevLocation } = this.state;
        prevLocation = data.location;
        if (data.location === 'APO/FPO') {
            prevStateAPO = data.state;
        } else {
            prevState = data.state;
        }
        data.suggestedAddress = 0;
        this.setState({ data, prevState, prevStateAPO, prevLocation, modalTitle });
    };

    componentDidMount = () => {
        document.addEventListener('mousedown', this.handleSuggestedAddressDismiss, true);
        document.addEventListener('keydown', this.handleSuggestedAddressTab, true);
    };

    componentWillUnmount = () => {
        document.removeEventListener('mousedown', this.handleSuggestedAddressDismiss, true);
        document.removeEventListener('keydown', this.handleSuggestedAddressTab, true);
        this.props.handleClearAddressVerification();
    };

    handleSuggestedAddressDismiss = (event) => {
        const { showSuggestedDropdown } = this.state;
        const streetAddressColumn = this.streetAddressColumnRef?.current;

        if (!showSuggestedDropdown || !streetAddressColumn) {
            return;
        }

        if (streetAddressColumn.contains(event.target)) {
            return;
        }

        this.hideSuggestedAddress();
    };

    handleSuggestedAddressTab = (event) => {
        const { showSuggestedDropdown } = this.state;
        const streetAddressColumn = this.streetAddressColumnRef?.current;

        if (!showSuggestedDropdown || event.key !== 'Tab' || !streetAddressColumn) {
            return;
        }

        if (!streetAddressColumn.contains(event.target)) {
            return;
        }

        this.hideSuggestedAddress();
    };

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillReceiveProps = (props) => {
        const {
            modal,
            selectedAddress,
            searchResults,
            isAddressVerification,
            verificationDecision,
            autoSaveAddress,
            verificationProvider,
            searchError,
        } = props;
        const {
            selectedSearchAddress,
            confirmAddress,
            isVerificationNeeded,
            suppressVerificationReopen,
        } = this.state;

        if (suppressVerificationReopen && !isAddressVerification) {
            this.setState({ suppressVerificationReopen: false });
        }

        if (
            this.isGiftListLoqateVerification(props)
            && !confirmAddress
            && this.state.showSuggestedDropdown
            && searchResults !== this.props.searchResults
        ) {
            this.setState({ loqateTypeaheadResults: searchResults });
        }

        if (
            this.isGiftListLoqateVerification(props)
            && confirmAddress
            && searchResults !== this.props.searchResults
            && (this.state.activeVerificationContainerId || this.state.verificationModalHistory.length > 0)
        ) {
            const defaultSelection = this.getDefaultVerificationSelection(searchResults);
            this.setState((prevState) => ({
                verificationModalResults: prevState.activeVerificationContainerId
                    ? searchResults.filter((item) => item.id !== prevState.activeVerificationContainerId)
                    : searchResults,
                activeVerificationContainerId: '',
                verificationDropdownOpen: true,
                isVerificationContainerLoading: false,
                data: {
                    ...prevState.data,
                    suggestedAddress: defaultSelection.suggestedAddress,
                },
                verificationSelection: {
                    type: defaultSelection.selectionType,
                    index: defaultSelection.selectionIndex,
                },
            }));
            return;
        }

        // Reset password fields
        if (!isEmpty(selectedAddress) && selectedSearchAddress) {
            const { data, errors } = this.state;
            data.street = selectedAddress.street;
            data.apt = selectedAddress.apt;
            data.city = selectedAddress.city;
            data.state = selectedAddress.state;
            data.zipcode = selectedAddress.zipcode;
            data.country = selectedAddress.country;
            delete errors.street;
            delete errors.city;
            delete errors.state;
            delete errors.zipcode;
            this.setState({ data, errors, selectedSearchAddress: false }, () => {
                if (!isVerificationNeeded) {
                    this.handleKeepAddressClicked();
                }
            });
            // this.setState({ isSubmited: false, data });
        } else if (
            this.isGiftListLoqateVerification(props)
            && isAddressVerification
            && isVerificationNeeded
            && !suppressVerificationReopen
        ) {
            if (!isEmpty(autoSaveAddress) && !confirmAddress) {
                this.applyVerificationAddressAndSubmit(autoSaveAddress);
                return;
            }

            if (searchError?.Message && !verificationDecision && !confirmAddress) {
                this.setState({ isVerificationNeeded: false }, () => {
                    this.handleKeepAddressClicked();
                });
                return;
            }

            if (confirmAddress && searchResults !== this.props.searchResults) {
                const defaultSelection = this.getDefaultVerificationSelection(searchResults);
                this.setState((prevState) => ({
                    verificationModalResults: prevState.activeVerificationContainerId
                        ? searchResults.filter((item) => item.id !== prevState.activeVerificationContainerId)
                        : searchResults,
                    activeVerificationContainerId: '',
                    verificationDropdownOpen: true,
                    isVerificationContainerLoading: false,
                    data: {
                        ...prevState.data,
                        suggestedAddress: defaultSelection.suggestedAddress,
                    },
                    verificationSelection: {
                        type: defaultSelection.selectionType,
                        index: defaultSelection.selectionIndex,
                    },
                }));
                return;
            }

            if (!confirmAddress) {
                const defaultSelection = this.getDefaultVerificationSelection(searchResults);
                this.setState((prevState) => ({
                    confirmAddress: true,
                    verificationModalResults: searchResults,
                    verificationModalHistory: [],
                    activeVerificationContainerId: '',
                    verificationDropdownOpen: false,
                    isVerificationContainerLoading: false,
                    data: {
                        ...prevState.data,
                        suggestedAddress: defaultSelection.suggestedAddress,
                    },
                    verificationSelection: {
                        type: defaultSelection.selectionType,
                        index: defaultSelection.selectionIndex,
                    },
                }));
            }
        } else if (
            props?.modal?.config !== 'GiftListRecipientForm'
            && isAddressVerification
            && !confirmAddress
            && isVerificationNeeded
        ) {
            const hasAddress = this.hasSuggestedAddress(searchResults);
            if (hasAddress) {
                // Keep Address
                this.setState({ isVerificationNeeded: false }, () => {
                    this.handleKeepAddressClicked();
                });
            } else {
                modal.title = 'Address Verification';
                modal.customClass = 'dialog no-x-button';
                this.props.handleUpdateModal(modal);
                this.setState({ confirmAddress: true });
            }
        }
    };

    getRelationship = (relationships, RelationShip) => {
        let id = '0';
        relationships.forEach((relationship) => {
            if (relationship.id === RelationShip) {
                id = RelationShip;
            }
        });
        return id;
    };

    isGiftListLoqateVerification = (props = this.props) => props?.modal?.config === 'GiftListRecipientForm'
        && props?.verificationProvider === 'loqate';

    getVerificationModalResults = () => this.state.verificationModalResults || [];

    getSelectedVerificationAddress = () => {
        const { verificationSelection } = this.state;
        const verificationResults = this.getVerificationModalResults();

        if (verificationSelection.type !== 'suggested' || verificationSelection.index === null) {
            return null;
        }

        return verificationResults[verificationSelection.index] || null;
    };

    getDefaultVerificationSelection = (results = []) => {
        if (!results.length) {
            return {
                suggestedAddress: '',
                selectionType: 'entered',
                selectionIndex: null,
            };
        }

        const firstAddressIndex = results.findIndex((item) => !item?.isContainer);
        const selectionIndex = firstAddressIndex >= 0 ? firstAddressIndex : 0;

        return {
            suggestedAddress: selectionIndex,
            selectionType: 'suggested',
            selectionIndex,
        };
    };

    resetVerificationModalState = () => {
        this.setState({
            confirmAddress: false,
            verificationModalResults: [],
            verificationModalHistory: [],
            activeVerificationContainerId: '',
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
            verificationSelection: {
                type: 'entered',
                index: null,
            },
        });
        this.props.handleClearAddressVerification();
    };

    applyVerificationAddressAndSubmit = (address = {}) => {
        const { data, errors } = this.state;
        const nextData = {
            ...data,
            street: address.addressLine1 || address.street || '',
            apt: address.addressLine2 || address.apt || '',
            city: address.city || '',
            state: address.state || '',
            zipcode: address.zipcode || address.zipCode || '',
            country: normalizeCountryCode(address.country || ''),
        };

        delete errors.street;
        delete errors.city;
        delete errors.state;
        delete errors.zipcode;

        this.setState({
            data: nextData,
            errors,
            confirmAddress: false,
            isVerificationNeeded: false,
            verificationModalResults: [],
            verificationModalHistory: [],
            activeVerificationContainerId: '',
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
            verificationSelection: {
                type: 'entered',
                index: null,
            },
        }, () => {
            this.props.handleClearAddressVerification();
            this.handleKeepAddressClicked();
        });
    };

    handleVerificationSelection = (address, index) => {
        if (address?.isContainer) {
            const { verificationSearchContext, verificationResult } = this.props;
            this.setState((prevState) => ({
                verificationModalHistory: [...prevState.verificationModalHistory, prevState.verificationModalResults],
                activeVerificationContainerId: address.id || '',
                verificationDropdownOpen: true,
                isVerificationContainerLoading: true,
            }), () => {
                this.props.handleSearchAddress({
                    provider: 'loqate',
                    query: verificationSearchContext?.query || '',
                    country: verificationSearchContext?.country || countryISOAlfa3Codes[this.state.data.country],
                    take: verificationSearchContext?.take || 25,
                    container: address.id,
                    includeContainers: true,
                    selectionSource: 'find',
                    verificationResult,
                });
            });
            return;
        }

        this.setState({
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
            verificationSelection: {
                type: 'suggested',
                index,
            },
        });
    };

    handleVerificationBack = () => {
        this.setState((prevState) => {
            if (prevState.verificationModalHistory.length === 0) {
                return null;
            }

            const verificationModalHistory = [...prevState.verificationModalHistory];
            const verificationModalResults = verificationModalHistory.pop();
            const defaultSelection = this.getDefaultVerificationSelection(verificationModalResults);

            return {
                verificationModalHistory,
                verificationModalResults,
                activeVerificationContainerId: '',
                verificationDropdownOpen: true,
                isVerificationContainerLoading: false,
                data: {
                    ...prevState.data,
                    suggestedAddress: defaultSelection.suggestedAddress,
                },
                verificationSelection: {
                    type: defaultSelection.selectionType,
                    index: defaultSelection.selectionIndex,
                },
            };
        });
    };

    handleVerificationDropdownChanged = (dataChanged) => {
        const selectedValue = dataChanged?.suggestedAddress;

        if (selectedValue === 'verification-back') {
            this.handleVerificationBack();
            return;
        }

        const selectedIndex = Number(selectedValue);
        if (Number.isNaN(selectedIndex)) {
            return;
        }

        const selectedAddress = this.state.verificationModalResults[selectedIndex];
        if (selectedAddress?.isContainer) {
            this.handleVerificationSelection(selectedAddress, selectedIndex);
            return;
        }

        this.setState({
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
        });
    };

    handleVerificationOptionClick = (option) => {
        if (!option) {
            return;
        }

        if (option.id === 'verification-back') {
            this.handleVerificationBack();
            return;
        }

        const selectedIndex = Number(option.id);
        if (Number.isNaN(selectedIndex)) {
            return;
        }

        const selectedAddress = this.state.verificationModalResults[selectedIndex];
        if (selectedAddress?.isContainer) {
            this.handleVerificationSelection(selectedAddress, selectedIndex);
        }
    };

    handleVerificationDropdownOpen = () => {
        this.setState({ verificationDropdownOpen: true });
    };

    handleVerificationDropdownClose = () => {
        if (this.state.isVerificationContainerLoading) {
            return;
        }

        this.setState({ verificationDropdownOpen: false });
    };

    handleUseSelectedVerificationAddress = () => {
        const selectedAddressToUse = this.getSelectedVerificationAddress();

        if (!selectedAddressToUse || selectedAddressToUse.isContainer) {
            return;
        }

        if (selectedAddressToUse.normalizedAddress) {
            this.applyVerificationAddressAndSubmit(selectedAddressToUse.normalizedAddress);
            return;
        }

        this.setState({
            selectedSearchAddress: true,
            isVerificationNeeded: false,
            confirmAddress: false,
            verificationModalResults: [],
            verificationModalHistory: [],
            activeVerificationContainerId: '',
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
        }, () => {
            this.props.handleClearAddressVerification();
            this.props.handleSelectAnAddress(selectedAddressToUse);
        });
    };

    handleChooseSelectedVerificationAddress = () => {
        const { data, verificationModalResults } = this.state;
        const selectedAddress = verificationModalResults[data.suggestedAddress];

        if (!selectedAddress) {
            return;
        }

        if (selectedAddress.isContainer) {
            this.handleVerificationSelection(selectedAddress, data.suggestedAddress);
            return;
        }

        this.setState({
            verificationSelection: {
                type: 'suggested',
                index: data.suggestedAddress,
            },
        }, this.handleUseSelectedVerificationAddress);
    };

    handleLocationChanged = (dataChanged) => {
        const data = { ...dataChanged };
        let { prevLocation } = this.state;
        const { prevState, prevStateAPO } = this.state;
        if (data.location === 'APO/FPO') {
            data.state = prevStateAPO;
            data.city = data.apofpo;
            const errors = {};
            if (!data.state) {
                errors.state = 'Required field';
            }
            this.setState({ data, errors });
        }
        if (data.location !== 'APO/FPO' && prevLocation === 'APO/FPO') {
            data.city = '';
            data.state = prevState;
            const errors = {};
            errors.city = 'Required field';
            if (!data.state) {
                errors.state = 'Required field';
            }
            this.setState({ data, errors });
        }
        prevLocation = data.location;
        this.setState({ prevLocation });
    };

    handleAPOFPOChanged = (dataChanged) => {
        const data = { ...dataChanged };
        data.city = data.apofpo;
        // data.prevCityAPO = data.city;
        this.setState({ data });
    };

    handleStateChanged = (dataChanged) => {
        const data = { ...dataChanged };
        let { prevState, prevStateAPO } = this.state;
        if (data.location === 'APO/FPO') {
            prevStateAPO = data.state;
        } else {
            prevState = data.state;
        }
        this.setState({ prevState, prevStateAPO });
    };

    handleCountryChanged = (dataChanged) => {
        const data = { ...dataChanged };
        data.location = 'Residence';
        data.state = '';
        data.zipcode = '';
        const errors = {
            state: 'Required field',
            zipcode: 'Required field',
        };
        if (data.city === 'APO' || data.city === '') {
            data.city = '';
            errors.city = 'Required field';
        }
        this.setState({ data, errors });
    };

    hasSuggestedAddress = (newSearchResults) => {
        let isSuggested = true;
        const { data } = this.state;
        const { selectedAddress, searchResults } = this.props;
        if (!isEmpty(selectedAddress)) {
            // Compare every input field with the selectedAddress
            Object.keys(selectedAddress).forEach((item) => {
                if (selectedAddress[item] !== data[item]) {
                    isSuggested = false;
                }
            });
        } else {
            isSuggested = false;
        }
        const fullAddress = this.getFullAddressString();
        const suggestedList = !isEmpty(newSearchResults) ? newSearchResults : searchResults;
        if (!isEmpty(suggestedList)) {
            // Compare full address with the suggestedList
            suggestedList.forEach((item) => {
                if (item.suggestion === fullAddress) {
                    isSuggested = true;
                }
            });
        }
        return isSuggested;
    };

    doSubmit = (submitData) => {
        const {
            modal: { config: templateName },
            recipient: { occasionId: occasionIdRecipient, orderItemId },
            occasionId,
            currentPage,
            recipient,
            searchError,
            featureFlags,
        } = this.props;
        const { data, confirmAddress, isVerificationNeeded } = this.state;
        const isMyAccountQasOn = featureFlags?.['is-my-account-qas-on'] || false;
        const qasError = searchError?.Message?.length > 0 || false;
        const { AddressId, AddressBookEntryId } = submitData;
        const recipientData = {
            addressEntry: {
                FirstName: data.firstname,
                LastName: data.lastname,
                EmployerName: data.company,
                NickName: data.firstname,
                RelationShip: data.relationship,
                PhoneNumber: data.phone ? data.phone : undefined,
            },
            address: {
                AddressType: data.location,
                AddressLineOne: data.street,
                AddressLineTwo: data.apt,
                City: data.city,
                StateProvince: data.state,
                PostalCode: data.zipcode,
                Country: data.country,
                IsBillingAddress: data.isbilling,
            },
        };
        if (isMyAccountQasOn && !qasError && !confirmAddress && isVerificationNeeded && !this.hasSuggestedAddress()) {
            // Confirm address layout
            this.verifyAddress();
        } else {
            this.setState({ isVerificationNeeded: false }, () => {
                submitData.handleSubmitAddNewRecipient(
                    recipientData,
                    AddressId,
                    AddressBookEntryId,
                    templateName,
                    (occasionId || occasionIdRecipient),
                    orderItemId,
                    currentPage,
                    recipient,
                );
            });
        }
    };

    getFullAddressString = () => {
        const { data } = this.state;
        let fullAddress = data.street;
        if (data.apt && data.apt.trim()) {
            fullAddress += ` ${data.apt.trim()}, `;
        } else {
            fullAddress += ', ';
        }
        if (data.city && data.city.trim()) {
            fullAddress += `${data.city.trim()} `;
        }
        if (data.state) {
            fullAddress += `${data.state} `;
        }
        if (data.zipcode) {
            fullAddress += data.zipcode;
        }
        return fullAddress;
    };

    verifyAddress = () => {
        const { data } = this.state;
        const {
            modal: { config },
        } = this.props;
        const isoCountryCode = countryISOAlfa3Codes[data.country];
        const params = {
            query: this.getFullAddressString(),
            country: isoCountryCode,
            take: 20,
        };
        if (config === 'GiftListRecipientForm') {
            params.provider = 'loqate';
            params.address = {
                address1: data.street,
                address2: data.apt,
                city: data.city,
                state: data.state,
                zipCode: data.zipcode,
                country: isoCountryCode,
            };
        }
        this.props.handleVerifyAddress(params);
    };

    showSuggestions = () => {
        const { data, showSuggestedDropdown } = this.state;
        const { searchResults } = this.props;
        const suggestionResults = this.isGiftListLoqateVerification()
            ? this.state.loqateTypeaheadResults
            : searchResults;

        if (!isEmpty(suggestionResults) && data.street && data.street.length > 3 && showSuggestedDropdown) {
            return (
                <div className="suggested-address-wrapper">
                    <div className="suggested-address">{
                        this.state.loqateTypeaheadHistory.length > 0 && this.isGiftListLoqateVerification() && (
                            <MenuItem
                                key="loqate-back"
                                onClick={this.handleTypeaheadBack}
                            >
                                Go Back To Previous List
                            </MenuItem>
                        )
                    }{
                        suggestionResults.map((address) => {
                            const display = this.isGiftListLoqateVerification()
                                ? getLoqateSuggestionDisplay(address)
                                : null;

                            return (
                                <MenuItem
                                    key={address.id || address.suggestion}
                                    onClick={() => this.handleSelectAnAddress(address)}
                                >
                                    {this.isGiftListLoqateVerification() ? (
                                        <div className="loqate-suggestion-row">
                                            <span className="loqate-suggestion-primary">{display.label}</span>
                                            {address.isContainer && (
                                                <span className="loqate-suggestion-meta">
                                                    {display.meta ? <span className="loqate-suggestion-count">{display.meta}</span> : null}
                                                    <ChevronRight className="loqate-suggestion-chevron" />
                                                </span>
                                            )}
                                        </div>
                                    ) : address.suggestion}
                                </MenuItem>
                            );
                        })
                    }
                    </div>
                </div>
            );
        }
        return null;
    }

    handleSelectAnAddress = (address) => {
        if (this.isGiftListLoqateVerification() && address?.isContainer) {
            const { data, loqateTypeaheadResults, loqateTypeaheadHistory } = this.state;
            const isoCountryCode = countryISOAlfa3Codes[data.country];

            this.setState({
                loqateTypeaheadHistory: [...loqateTypeaheadHistory, loqateTypeaheadResults],
            });
            this.props.handleSearchAddress({
                provider: 'loqate',
                query: `${data.street.trim()}, ${data.city.trim()} ${data.state} ${data.zipcode}`.trim(),
                country: isoCountryCode,
                take: 25,
                includeContainers: true,
                container: address.id,
                selectionSource: 'find',
            });
            return;
        }

        this.setState({ selectedSearchAddress: true }, () => {
            this.props.handleSelectAnAddress(address);
        });
    }

    handleTypeaheadBack = () => {
        this.setState((prevState) => {
            if (prevState.loqateTypeaheadHistory.length === 0) {
                return null;
            }

            const loqateTypeaheadHistory = [...prevState.loqateTypeaheadHistory];
            const loqateTypeaheadResults = loqateTypeaheadHistory.pop();

            return {
                loqateTypeaheadHistory,
                loqateTypeaheadResults,
            };
        });
    };

    handleStreetKeyPress = (input) => {
        const { data, showSuggestedDropdown } = this.state;
        const {
            modal: { config },
        } = this.props;
        const street = input.value;
        // Show suggested address when user used tab key
        if (!showSuggestedDropdown) {
            this.setState({ showSuggestedDropdown: true });
        }
        if (street.length > 3 && street.trim()) {
            const isoCountryCode = countryISOAlfa3Codes[data.country];
            const params = {
                query: config === 'GiftListRecipientForm' ? `${street}, ${data.city.trim()} ${data.state} ${data.zipcode}` : street,
                country: isoCountryCode,
                take: 25,
            };
            if (config === 'GiftListRecipientForm') {
                params.provider = 'loqate';
                params.includeContainers = true;
                this.setState({ loqateTypeaheadHistory: [] });
            }
            this.props.handleSearchAddress(params);
        }
    };

    hideSuggestedAddress = () => {
        this.setState({
            showSuggestedDropdown: false,
            loqateTypeaheadResults: [],
            loqateTypeaheadHistory: [],
        });
    };

    showSuggestedAddress = (input) => {
        const { showSuggestedDropdown } = this.state;
        if (!showSuggestedDropdown) {
            this.setState({ showSuggestedDropdown: true }, () => {
                this.handleStreetKeyPress(input);
            });
        }
    };

    handleAddressBookChange = (selectedRecipient) => {
        let isRecipientSelected = false;
        if (!isEmpty(selectedRecipient) && selectedRecipient?.addressbook !== '0') isRecipientSelected = true;
        this.setState({ selectedRecipient, isRecipientSelected });
    };

    addressFormLayout = () => {
        const { isReadyState, classes, handleSubmitAddNewRecipient, handleCloseModal, featureFlags, handleDesktopRemoveAddressClicked } = this.props;
        let { locations } = this.props;
        const {
            AddressId,
            recipient,
            states,
            countries,
            AddressBookEntryId,
            occasionId,
            flags,
        } = this.props;
        const { APOFPO, relationships } = this.props;
        const {
            data, inputConfig, isRecipientSelected, activeTab, confirmAddress,
        } = this.state;
        let {
            modal: { title: modalTitle },
        } = this.props;
        const giftListV2 = featureFlags['is-giftlist-v2-enabled'] && flags?.experimentGiftListV2 === 'variant';

        let submitButtonLabel = '';
        if (modalTitle) {
            modalTitle = modalTitle.replace(/^(Edit |Add )/, '').trim();
        }
        const renderButtonTitle = () => {
            let title = ''.concat('Save ', modalTitle);
            if (giftListV2 && !confirmAddress) {
                title = 'Save Recipient';
            }
            return title;
        };
        // if (isEmpty(recipient) || !AddressId) {
        if (isEmpty(recipient)) {
            submitButtonLabel = isReadyState
                ? ''.concat('Add ', modalTitle)
                : 'Adding...';
        } else {
            submitButtonLabel = isReadyState
                ? renderButtonTitle()
                : 'Saving...';
        }
        let stateByAddressType = states[data.country].filter((s) => s.id !== 'AA' && s.id !== 'AE' && s.id !== 'AP');
        if (data.location === 'APO/FPO' && data.country === 'US') {
            stateByAddressType = states[data.country].filter((s) => s.id === 'AA' || s.id === 'AE' || s.id === 'AP');
        }
        let inputLabels = {};
        if (Object.keys(inputConfig).indexOf(data.location) > -1) {
            inputLabels = inputConfig[data.location];
        } else {
            inputLabels = inputConfig.default;
        }

        const inputLabelsByCountry = {};
        inputLabelsByCountry.state = 'State';
        inputLabelsByCountry.zipcode = 'Zip Code';
        if (data.country !== 'US') {
            inputLabelsByCountry.state = 'Province';
            inputLabelsByCountry.zipcode = 'Postal Code';
        }
        if (data.country === 'CA') {
            locations = locations.filter((l) => l.id !== 'APO/FPO');
        }

        const zipCodeByCountry = {};
        zipCodeByCountry.type = 'tel';
        zipCodeByCountry.regex = '^[0-9\\-]*$';
        zipCodeByCountry.mask = '';
        if (data.country !== 'US') {
            zipCodeByCountry.type = 'text';
            zipCodeByCountry.regex = '^[0-9\\-a-zA-Z\\s]*$';
        }
        if (data.country === 'CA') {
            zipCodeByCountry.mask = CanadaZipCodeMask;
        }

        const submitData = {
            AddressId,
            AddressBookEntryId,
            recipient,
            handleSubmitAddNewRecipient,
        };
        const dataTestPrefix = 'my-account-add-address-';

        return (
            <>
                {giftListV2 && isEmpty(recipient) && occasionId && !confirmAddress && (
                    <Tabs scrollButtons="false" className={classes.tabContainer}>
                        <Tab label="ADD A NEW ADDRESS"  id="tab1" onClick={() => { this.setState({ activeTab: 'tab1' }); }} className={activeTab === 'tab1' ? 'activeTab tabButton' : 'tabButton'} />
                        <Tab label="Select From Contacts" id="tab2"  onClick={() => { this.setState({ activeTab: 'tab2' }); }} className={activeTab === 'tab2' ? 'activeTab tabButton' : 'tabButton'} />
                    </Tabs>
                )}
                {isEmpty(recipient) && occasionId && !confirmAddress ? ( // show select address dropdown only for gift list
                    <>
                        <DesktopSelectAddressDropdown onChange={this.handleAddressBookChange} activeTab={activeTab} giftListV2={giftListV2} />
                        {!giftListV2 && <Divider text="or" customClass={`${(isRecipientSelected) ? 'display-none' : ''}`} />}
                    </>

                ) : ''}
                {((activeTab === 'tab1' && giftListV2) || !giftListV2) && (
                    <form
                        onSubmit={(event) => this.handleSubmit(event, submitData)}
                        className={`${classes.form} ${giftListV2 ? classes.giftListV2 : ''} margin-top ${(isRecipientSelected || confirmAddress) ? 'display-none' : ''}`}
                    >
                        <div className="split">
                            {giftListV2 && (
                                <div className="locationType">
                                    {this.renderSelect({
                                        name: 'location',
                                        label: 'Location Type *',
                                        options: locations,
                                        desktop: true,
                                        onChangeHandler: this.handleLocationChanged,
                                        onClickHandler: this.hideSuggestedAddress,
                                    })}
                                </div>
                            )}
                            {this.renderInput({
                                name: 'firstname',
                                dataTest: { field: `${dataTestPrefix}${'field-firstname'}`, label: `${dataTestPrefix}${'label-firstname'}` },
                                label: `${inputLabels.firstname.label} *`,
                                type: 'text',
                                pattern: '^[a-zA-Z\\s\\.\\-&]*$',
                                length: 25,
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                            {this.renderInput({
                                name: 'lastname',
                                dataTest: { field: `${dataTestPrefix}${'field-lastname'}`, label: `${dataTestPrefix}${'label-lastname'}` },
                                label: `${inputLabels.lastname.label} *`,
                                type: 'text',
                                pattern: '^[a-zA-Z\\s\\.\\-&]*$',
                                length: 25,
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                        </div>
                        <div className="split">
                            <div className={inputLabels.relationship.class}>
                                {this.renderSelect({
                                    name: 'relationship',
                                    label: 'Relationship *',
                                    options: relationships,
                                    desktop: true,
                                    onClickHandler: this.hideSuggestedAddress,
                                })}
                            </div>
                            <div className={`${inputLabels.location.class} locationSelect`}>
                                {this.renderSelect({
                                    name: 'location',
                                    label: 'Location Type *',
                                    options: locations,
                                    desktop: true,
                                    onChangeHandler: this.handleLocationChanged,
                                    onClickHandler: this.hideSuggestedAddress,
                                })}
                            </div>
                        </div>
                        <div className={inputLabels.company.class}>
                            {this.renderInput({
                                name: 'company',
                                dataTest: { field: `${dataTestPrefix}${'field-company'}`, label: `${dataTestPrefix}${'label-company'}` },
                                label: `${inputLabels.company.label}`,
                                type: 'text',
                                length: 100,
                                adornment: '',
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                        </div>
                        <div className="split">
                            <div className="street-address-column" ref={this.streetAddressColumnRef}>
                                {this.renderInput({
                                    name: 'street',
                                    dataTest: { field: `${dataTestPrefix}${'field-street1'}`, label: `${dataTestPrefix}${'label-street1'}` },
                                    label: `${inputLabels.street.label} *`,
                                    type: 'text',
                                    length: 100,
                                    autoComplete: 'disabled',
                                    desktop: true,
                                    onKeyupHandler: this.handleStreetKeyPress,
                                    onClickHandler: this.showSuggestedAddress,
                                })}
                                {this.showSuggestions()}
                            </div>
                            {this.renderInput({
                                name: 'apt',
                                label: inputLabels.apt.label,
                                dataTest: { field: `${dataTestPrefix}${'field-street2'}`, label: `${dataTestPrefix}${'label-street2'}` },
                                type: 'text',
                                length: 20,
                                autoComplete: 'disabled',
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                        </div>
                        <div className="split cityContainer">
                            <div className={inputLabels.apofpo.class}>
                                {this.renderSelect({
                                    name: 'apofpo',
                                    label: 'APO/FPO *',
                                    options: APOFPO,
                                    desktop: true,
                                    onChangeHandler: this.handleAPOFPOChanged,
                                    onClickHandler: this.hideSuggestedAddress,
                                })}
                            </div>
                            <div className={inputLabels.city.class}>
                                {this.renderInput({
                                    name: 'city',
                                    dataTest: { field: `${dataTestPrefix}${'field-city'}`, label: `${dataTestPrefix}${'label-city'}` },
                                    label: `${inputLabels.city.label} *`,
                                    type: 'text',
                                    pattern: '^[a-zA-Z\\s\\.\\-]*$',
                                    length: 50,
                                    autoComplete: 'disabled',
                                    desktop: true,
                                    onClickHandler: this.hideSuggestedAddress,
                                })}
                            </div>
                            {this.renderSelect({
                                name: 'state',
                                label: `${inputLabelsByCountry.state} *`,
                                options: stateByAddressType,
                                onChangeHandler: this.handleStateChanged,
                                useIDAsLabel: true,
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                                addressBookSelect: true,
                            })}
                        </div>
                        <div className="split">
                            {this.renderInput({
                                name: 'zipcode',
                                dataTest: { field: `${dataTestPrefix}${'field-zipcode'}`, label: `${dataTestPrefix}${'label-zipcode'}` },
                                label: `${inputLabelsByCountry.zipcode} *`,
                                type: zipCodeByCountry.type,
                                pattern: zipCodeByCountry.regex,
                                length: 10,
                                mask: zipCodeByCountry.mask,
                                autoComplete: 'disabled',
                                desktop: true,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                            {this.renderSelect({
                                name: 'country',
                                label: 'Country *',
                                options: countries,
                                desktop: true,
                                onChangeHandler: this.handleCountryChanged,
                                onClickHandler: this.hideSuggestedAddress,
                            })}
                            {giftListV2 &&   (
                                <div className="locationType">
                                    {this.renderInput({
                                        name: 'phone',
                                        dataTest: { field: `${dataTestPrefix}${'field-phone'}`, label: `${dataTestPrefix}${'label-phone'}` },
                                        label: inputLabels.phone.label,
                                        type: 'tel',
                                        pattern: '^[0-9]*$',
                                        length: null,
                                        adornment: '',
                                        mask: PhoneMask,
                                        desktop: true,
                                        onClickHandler: this.hideSuggestedAddress,
                                    })}
                                </div>
                            )}
                        </div>
                        {!giftListV2 && (
                            <div className={inputLabels.phone.label}>
                                {this.renderInput({
                                    name: 'phone',
                                    dataTest: { field: `${dataTestPrefix}${'field-phone'}`, label: `${dataTestPrefix}${'label-phone'}` },
                                    label: inputLabels.phone.label,
                                    type: 'tel',
                                    pattern: '^[0-9]*$',
                                    length: null,
                                    adornment: '',
                                    mask: PhoneMask,
                                    desktop: true,
                                    onClickHandler: this.hideSuggestedAddress,
                                })}
                            </div>
                        )}
                        {!giftListV2 ? (
                            <div className="margin-top-2x">
                                {this.renderButton({
                                    label: submitButtonLabel,
                                    dataTest: { field: `${dataTestPrefix}${submitButtonLabel.split(' ').join('-').toLowerCase()}` },
                                    className: 'no-margin',
                                    onClick: null,
                                    type: 'submit',
                                })}
                                {this.renderButton({
                                    label: 'Cancel',
                                    dataTest: { field: `${dataTestPrefix}${''.concat('Cancel ', modalTitle).split(' ').join('-').toLowerCase()}` },
                                    className: 'secondary',
                                    onClick: handleCloseModal,
                                    type: 'button',
                                    isValidated: false,
                                })}
                            </div>
                        )
                            : (
                                <div className={`${classes.adressButtonContainer} ${confirmAddress && !isEmpty(recipient) ? classes.confirmButtonAddress : ''}`}>
                                    {isEmpty(recipient) || confirmAddress
                                        ? ''
                                        : (
                                            <div className={classes.addressButtonRemove}>
                                                <Button
                                                    variant="contained"
                                                    className="removeButton"
                                                    dataTest={{ field: `${dataTestPrefix}${''.concat('Cancel ', modalTitle).split(' ').join('-').toLowerCase()}` }}
                                                    onClick={() => handleDesktopRemoveAddressClicked(recipient, null, 'GIFT_LIST')}
                                                >
                                                    <strong> Remove from list</strong>
                                                </Button>

                                            </div>
                                        )}
                                    <div className={classes.buttonContainer}>
                                        {this.renderButton({
                                            label: 'Cancel',
                                            dataTest: { field: `${dataTestPrefix}${''.concat('Cancel ', modalTitle).split(' ').join('-').toLowerCase()}` },
                                            className: 'cancelButton',
                                            onClick: handleCloseModal,
                                            type: 'button',
                                            isValidated: false,
                                        })}
                                        {this.renderButton({
                                            label: submitButtonLabel,
                                            dataTest: { field: `${dataTestPrefix}${submitButtonLabel.split(' ').join('-').toLowerCase()}` },
                                            className: 'submitButton',
                                            onClick: null,
                                            type: 'submit',
                                        })}
                                    </div>
                                </div>
                            )}
                    </form>
                )}
            </>
        );
    };

    confirmationLayout = () => {
        const {
            data,
            confirmAddress,
            isVerificationNeeded,
            verificationSelection,
        } = this.state;
        const {
            searchResults,
            verificationDecision,
            verificationResult,
        } = this.props;
        if (!confirmAddress || !data.firstname) return null;

        if (this.isGiftListLoqateVerification()) {
            const verificationResults = this.getVerificationModalResults();
            const selectedVerificationAddress = this.getSelectedVerificationAddress();
            const hasVerificationSuggestions = verificationResults.length > 0;
            const isAcceptWithChanges = verificationDecision === 'accept' && verificationResult?.hasAddressChanges;
            let modalTitle = 'We couldn\'t verify your address.';
            if (isAcceptWithChanges) {
                modalTitle = 'Confirm your address';
            } else if (verificationResult?.reason === INVALID_SECONDARY_UNIT_REASON) {
                modalTitle = 'Please confirm your unit / apt / suite number';
            }
            const enteredAddress = buildAddressDisplay({
                street: data.street,
                apt: data.apt,
                city: data.city,
                state: data.state,
                zipcode: data.zipcode,
                country: data.country,
            });
            const suggestedAddress = buildAddressDisplay(selectedVerificationAddress?.normalizedAddress || verificationResults[0]?.normalizedAddress || {});
            const suggestedList = [
                ...(this.state.verificationModalHistory.length > 0 ? [{
                    id: 'verification-back',
                    name: 'Go Back To Previous List',
                }] : []),
                ...verificationResults.map((address, index) => ({
                    id: index,
                    name: getLoqateVerificationOptionLabel(address),
                })),
            ];

            return (
                <div className={`${!confirmAddress ? 'display-none' : ''}`}>
                    {isAcceptWithChanges && <p className="margin-bottom-2x">Updated fields are highlighted below. Please choose which address you want to use.</p>}
                    {!isAcceptWithChanges && !hasVerificationSuggestions && <p className="bold margin-top h2">{modalTitle}</p>}

                    {isAcceptWithChanges && (
                        <>
                            <div className="bold margin-top">WE SUGGEST:</div>
                            <div className="margin-top margin-bottom-2x">
                                <div>{suggestedAddress.primaryLine}</div>
                                {suggestedAddress.tertiaryLine ? <div>{suggestedAddress.tertiaryLine}</div> : null}
                                <div>{suggestedAddress.secondaryLine}</div>
                            </div>
                            <Divider text="or" />
                            <div className="bold margin-top">KEEP ADDRESS AS ENTERED:</div>
                            <div className="margin-top margin-bottom-2x">
                                <div>{enteredAddress.primaryLine}</div>
                                {enteredAddress.tertiaryLine ? <div>{enteredAddress.tertiaryLine}</div> : null}
                                <div>{enteredAddress.secondaryLine}</div>
                            </div>
                            <div className="align-center">
                                <Button
                                    variant="contained"
                                    className="margin-bottom-2x"
                                    onClick={this.handleUseSelectedVerificationAddress}
                                >
                                    Use Suggested Address
                                </Button>
                                <Button
                                    variant="contained"
                                    className="secondary margin-left-8x"
                                    onClick={() => this.handleKeepAddressClicked()}
                                >
                                    Keep Address As Entered
                                </Button>
                            </div>
                        </>
                    )}

                    {!isAcceptWithChanges && hasVerificationSuggestions && (
                        <>
                            <p className="margin-bottom-2x">
                                {verificationResult?.reason === INVALID_SECONDARY_UNIT_REASON
                                    ? 'Please review the recommended unit / apt / suite options below.'
                                    : 'According to the USPS&reg; the street number you entered is either missing or incorrect. Please refer to the list of options below.'}
                            </p>
                            <p className="h2 no-margin-bottom">Choose Recommended Address Below:</p>
                            <div>
                                {this.renderSelect({
                                    name: 'suggestedAddress',
                                    label: '',
                                    options: suggestedList,
                                    onChangeHandler: this.handleVerificationDropdownChanged,
                                    onOptionClick: this.handleVerificationOptionClick,
                                    onClickHandler: this.hideSuggestedAddress,
                                    onOpenHandler: this.handleVerificationDropdownOpen,
                                    onCloseHandler: this.handleVerificationDropdownClose,
                                    open: this.state.verificationDropdownOpen,
                                    addressBookSelect: true,
                                })}
                            </div>
                            <div className="align-center margin-top-2x margin-bottom-2x">
                                <Button
                                    variant="contained"
                                    className="verificationChooseAddressButton"
                                    onClick={this.handleChooseSelectedVerificationAddress}
                                >
                                    Choose Address
                                </Button>
                            </div>
                            <Divider text="or" />
                            <p className="bold margin-top h2">Keep or edit original address:</p>
                        </>
                    )}

                    {!isAcceptWithChanges && (
                        <>
                            <div className="margin-top margin-bottom-2x">
                                <div>{enteredAddress.primaryLine}</div>
                                {enteredAddress.tertiaryLine ? <div>{enteredAddress.tertiaryLine}</div> : null}
                                <div>{enteredAddress.secondaryLine}</div>
                            </div>
                            {!hasVerificationSuggestions && <p className="bold">Please choose one of the options below to proceed</p>}
                            <div className="align-center verificationActionRow">
                                <Button
                                    variant="contained"
                                    className="secondary verificationEditAddressButton"
                                    onClick={this.handleEditAddressClicked}
                                >
                                    Edit Address
                                </Button>
                                <Button
                                    variant="contained"
                                    className="verificationKeepAddressButton"
                                    onClick={() => this.handleKeepAddressClicked()}
                                >
                                    {hasVerificationSuggestions ? 'Keep Address' : 'Use Address As Entered'}
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            );
        }

        const isoCountryCode = countryISOAlfa3Codes[data.country];
        const address = <div className="margin-top-2x margin-bottom-2x"><div>{data.street} {data.apt}</div><div>{data.city}, {data.state} {data.zipcode}</div><div>{isoCountryCode}</div></div>;
        const suggestedList = [];
        if (!isEmpty(searchResults)) {
            searchResults.forEach((item, index) => {
                suggestedList.push({ id: index, name: item.suggestion });
            });
        }
        let textInfo = isEmpty(suggestedList) ? (<p className="bold margin-top">We could not find a match to the address:</p>) : '';
        textInfo = !isVerificationNeeded ? (<p className="bold margin-top">You have selected address:</p>) : textInfo;
        let textOptions = isEmpty(suggestedList) ? (<p className="bold">Please choose one of the options below to proceed</p>) : '';
        textOptions = isVerificationNeeded ? textOptions : '';
        return (
            <>
                <div className={`${!confirmAddress ? 'display-none' : ''}`}>
                    {textInfo}
                    {!isEmpty(suggestedList) ? (
                        <>
                            <p className="margin-bottom-2x">According to the USPS&reg; the street number you entered is either missing or incorrect. Please refer to the list of options below.</p>
                            <p className="h2 no-margin-bottom">Choose Recommended Address Below:</p>
                            <div className="align-center">
                                {this.renderSelect({
                                    name: 'suggestedAddress',
                                    label: '',
                                    options: suggestedList,
                                    onChangeHandler: this.handleLocationChanged,
                                    onClickHandler: this.hideSuggestedAddress,
                                    addressBookSelect: true,
                                })}
                                <div className="margin-top-2x">
                                    <Button
                                        variant="contained"
                                        className=""
                                        onClick={() => this.handleChooseSeletedAddress(searchResults)}
                                    >
                                        Choose Address
                                    </Button>
                                </div>
                            </div>
                            <Divider text="or" />
                            <p className="bold margin-top h2">Keep or edit original address:</p>
                        </>
                    ) : ''}
                    {address}
                    {textOptions}
                    <div className="align-center">
                        <Button
                            variant="contained"
                            className={`margin-top ${isVerificationNeeded ? 'margin-bottom-2x' : ''}`}
                            onClick={() => this.handleEditAddressClicked()}
                        >
                            {!isVerificationNeeded ? 'Saving Address...' : 'Edit Address'}
                        </Button>
                        <Button
                            variant="contained"
                            className={`secondary margin-top margin-bottom-2x margin-left-8x ${!isVerificationNeeded ? 'display-none' : ''}`}
                            onClick={() => this.handleKeepAddressClicked()}
                        >
                            Keep Address
                        </Button>
                    </div>
                </div>
            </>
        );
    };

    handleEditAddressClicked = () => {
        const { modal } = this.props;
        const { data, modalTitle } = this.state;
        data.suggestedAddress = 0;
        this.setState({
            data,
            confirmAddress: false,
            isVerificationNeeded: true,
            verificationModalResults: [],
            verificationModalHistory: [],
            activeVerificationContainerId: '',
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
            verificationSelection: {
                type: 'entered',
                index: null,
            },
            suppressVerificationReopen: true,
        }, () => {
            this.props.handleUpdateModal({
                ...modal,
                title: modalTitle,
                customClass: 'dialog',
            });
            this.props.handleClearAddressVerification();
        });
    };

    handleKeepAddressClicked = () => {
        // this.setState({ confirmAddress: false });
        const { AddressId, AddressBookEntryId, recipient, handleSubmitAddNewRecipient } = this.props;
        const { data } = this.state;
        data.suggestedAddress = 0;
        const submitData = {
            AddressId,
            AddressBookEntryId,
            recipient,
            handleSubmitAddNewRecipient,
        };
        this.setState({
            data,
            isVerificationNeeded: false,
            confirmAddress: false,
            verificationModalResults: [],
            verificationModalHistory: [],
            activeVerificationContainerId: '',
            verificationDropdownOpen: false,
            isVerificationContainerLoading: false,
            verificationSelection: {
                type: 'entered',
                index: null,
            },
        }, () => {
            this.props.handleClearAddressVerification();
            this.doSubmit(submitData);
        });
    };

    handleChooseSeletedAddress = (searchResults) => {
        const { suggestedAddress } = this.state.data;
        if (!isEmpty(searchResults) && searchResults[suggestedAddress].format) {
            const address = searchResults[suggestedAddress];
            // Get select address and submit data
            this.setState({ isVerificationNeeded: false }, () => {
                this.handleSelectAnAddress(address);
            });
        }
    };

    render() {
        const { inputConfig } = this.state;
        if (!inputConfig) return null;
        return (
            <>
                {this.addressFormLayout()}
                {this.confirmationLayout()}
            </>
        );
    }
}

DesktopAddressForm.defaultProps = {
    AddressId: undefined,
    AddressBookEntryId: undefined,
    recipient: {},
    locations: [],
    states: {},
    countries: [],
    APOFPO: [],
    relationships: [],
    selectedAddress: {},
    searchResults: [],
    searchError: { Message: '' },
    verificationResult: {},
    verificationDecision: '',
    autoSaveAddress: {},
    verificationSearchContext: {},
    verificationProvider: '',
    handleSubmitAddNewRecipient: () => { },
    handleSearchAddress: () => { },
    isAddressVerification: false,
    handleDesktopRemoveAddressClicked: () => { },
    handleClearAddressVerification: () => { },
    flags: {},
};

DesktopAddressForm.propTypes = {
    classes: object.isRequired,
    isReadyState: bool.isRequired,
    AddressId: number,
    AddressBookEntryId: oneOfType([
        number,
        string,
    ]),
    recipient: shape({
        AddressBookEntryId: oneOfType([
            number,
            string,
        ]),
        ContactId: number,
        FirstName: string,
        LastName: string,
        NickName: string,
        LastUpdateDate: string,
        NumberOfAddresses: number,
        Addresses: arrayOf(
            shape({
                AddressId: number,
                AddressBookEntryId: oneOfType([
                    number,
                    string,
                ]),
                AddressType: string,
                AddressLineOne: string,
                AddressLineTwo: string,
                City: string,
                StateProvince: string,
                PostalCode: string,
                Country: string,
                ContactId: number,
            }),
        ),
    }),
    locations: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    states: shape({
        US: arrayOf(
            shape({
                id: string,
                name: string,
            }),
        ),
        CA: arrayOf(
            shape({
                id: string,
                name: string,
            }),
        ),
    }),
    countries: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    APOFPO: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    relationships: arrayOf(
        shape({
            id: string,
            name: string,
        }),
    ),
    selectedAddress: shape({
        street: string,
        apt: string,
        city: string,
        state: string,
        country: string,
        zipcode: string,
    }),
    searchResults: arrayOf(
        shape({
            suggestion: string,
            matched: array,
            format: oneOfType([string, bool]),
        }),
    ),
    searchError: object,
    handleSubmitAddNewRecipient: func,
    handleSearchAddress: func,
    handleCloseModal: func,
    isAddressVerification: bool,
    verificationResult: object,
    verificationDecision: string,
    autoSaveAddress: object,
    verificationSearchContext: object,
    verificationProvider: string,
    activeABTests: object.isRequired,
    featureFlags: object,
    handleDesktopRemoveAddressClicked: func,
    handleVerifyAddress: func.isRequired,
    handleSetVerificationState: func.isRequired,
    handleSelectAnAddress: func.isRequired,
    handleUpdateModal: func.isRequired,
    handleClearAddressVerification: func,
    flags: object,
};

const mapStateToProps = (state) => {
    /* istanbul ignore next */
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    let selectedAddress = {};
    let searchResults = [];
    let searchError = { Message: '' };
    let isAddressVerification = false;
    let verificationResult = {};
    let verificationDecision = '';
    let autoSaveAddress = {};
    let verificationSearchContext = {};
    let verificationProvider = '';
    let featureFlags = {};
    try {
        ({
            selectedAddress,
            searchResults,
            searchError,
            isAddressVerification,
            verificationResult,
            verificationDecision,
            autoSaveAddress,
            verificationSearchContext,
            provider: verificationProvider,
        } = newState.common.addressVerification);
        featureFlags = { ...getFeatureFlags(state) };
        // eslint-disable-next-line no-empty
    } catch (ex) { }
    return {
        isReadyState: newState.isReadyState,
        AddressId: newState.AddressId,
        AddressBookEntryId: newState.AddressBookEntryId,
        occasionId: newState.giftList.occasionId,
        recipient: newState.recipient,
        locations: newState.locations,
        states: newState.states,
        countries: newState.countries,
        APOFPO: newState.APOFPO,
        relationships: newState.relationships,
        modal: newState.modal,
        currentPage: newState.giftList.currentPage,
        selectedAddress,
        featureFlags,
        searchError,
        searchResults,
        isAddressVerification,
        verificationResult,
        verificationDecision,
        autoSaveAddress,
        verificationSearchContext,
        verificationProvider,
    };
};

const mapDispatchToProps = (dispatch) => ({
    handleSubmitAddNewRecipient: (data, AddressId, AddressBookEntryId, templateName, occasionId, orderItemId, currentPage, recipient) => dispatch(EventDispatcher.handleSubmitAddNewRecipient(
        data,
        AddressId,
        AddressBookEntryId,
        templateName,
        occasionId,
        orderItemId,
        currentPage,
        recipient,
    )),
    handleSearchAddress: (params) => dispatch(EventDispatcher.handleSearchAddress(params)),
    handleVerifyAddress: (params) => dispatch(EventDispatcher.handleVerifyAddress(params)),
    handleSetVerificationState: (isVerified) => dispatch(EventDispatcher.handleSetVerificationState(isVerified)),
    handleSelectAnAddress: (address) => dispatch(EventDispatcher.handleSelectAnAddress(address)),
    handleUpdateModal: (modal) => dispatch(EventDispatcher.handleUpdateModal(modal)),
    handleClearAddressVerification: () => dispatch(EventDispatcher.handleClearAddressVerification()),
    handleCloseModal: () => dispatch(EventDispatcher.handleCloseModal()),
    handleDesktopRemoveAddressClicked: (recipient, AddressId, addressType) => dispatch(EventDispatcher.handleDesktopRemoveAddressClicked(
        recipient,
        AddressId,
        addressType,
    )),
    // resetAutoSearch: () => dispatch(EventDispatcher.resetAutoSearch()),
});
const enhance = compose(
    withLDConsumer(),
    withStyles(styles, { withTheme: true }),
    connect(mapStateToProps, mapDispatchToProps),

);

export default enhance(DesktopAddressForm);
